
// PHASE 3: Smart Biography Processing and Optimization with AI
// Now uses backend AI service (Claude) for therapeutic restructuring

interface BiographyOptimizationResponse {
  optimized_biography: string;
  original_length: number;
  optimized_length: number;
  processing_time_ms: number;
  cache_hit: boolean;
}

/**
 * Optimize a biography for therapeutic session integration using AI.
 *
 * This function calls the backend AI service which uses Claude to intelligently
 * restructure the biography, organizing content by:
 * - Relationships & support systems
 * - Safe spaces & environments
 * - Life challenges & stressors
 * - Goals & aspirations
 *
 * The output is written in third person for natural session integration.
 */
export const optimizeBiographyForDeepIntegration = async (
  biography: string,
  _categoryId: string,  // Kept for backward compatibility, not used by AI optimization
  _sessionType: string  // Kept for backward compatibility, not used by AI optimization
): Promise<string> => {

  // Skip for empty or very short biographies
  if (!biography || biography.trim().length < 50) {
    console.log('ℹ️ Skipping optimization - biography too short');
    return biography;
  }

  console.log('🤖 AI BIOGRAPHY OPTIMIZATION: Starting...', {
    originalLength: biography.length
  });

  const backendUrl = import.meta.env.VITE_BACKEND_URL;

  const response = await fetch(`${backendUrl}/biographies/optimize`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      biography,
      target_token_count: 200
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.detail || `HTTP ${response.status}`;

    console.error('❌ AI BIOGRAPHY OPTIMIZATION FAILED:', errorMessage);

    // Throw error with status code info for upstream handling
    const error = new Error(errorMessage) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  const data: BiographyOptimizationResponse = await response.json();

  console.log('✅ AI BIOGRAPHY OPTIMIZATION COMPLETE:', {
    originalLength: data.original_length,
    optimizedLength: data.optimized_length,
    compressionRatio: (data.optimized_length / data.original_length).toFixed(2),
    processingTimeMs: data.processing_time_ms,
    cacheHit: data.cache_hit
  });

  return data.optimized_biography;
};

// Enhanced biography validation for quality assurance
export const validateBiographyQuality = (
  biography: string,
  categoryId: string
): {
  isHighQuality: boolean;
  qualityScore: number;
  recommendations: string[];
} => {
  
  const recommendations: string[] = [];
  let qualityScore = 0;
  
  // Check for essential elements
  const hasRelationships = /family|friend|partner|spouse|child|parent|pet/i.test(biography);
  const hasPersonalSpaces = /home|bedroom|house|kitchen|garden|office/i.test(biography);
  const hasEmotionalContent = /feel|emotion|love|happy|sad|stressed|calm/i.test(biography);
  const hasWorkContext = /work|job|boss|career|office|colleague/i.test(biography);
  const hasGoals = /goal|dream|want|hope|aspiration|future|plan/i.test(biography);
  
  // Calculate quality score
  if (hasRelationships) qualityScore += 25;
  else recommendations.push('Add relationship information for deeper personalization');
  
  if (hasPersonalSpaces) qualityScore += 20;
  else recommendations.push('Include personal space details for better grounding');
  
  if (hasEmotionalContent) qualityScore += 20;
  else recommendations.push('Add emotional context for more meaningful sessions');
  
  if (hasWorkContext) qualityScore += 15;
  else recommendations.push('Include work situation for stress-relief sessions');
  
  if (hasGoals) qualityScore += 20;
  else recommendations.push('Add personal goals for motivational elements');
  
  // Check biography length
  if (biography.length < 100) {
    recommendations.push('Biography is too short - add more personal details');
    qualityScore -= 10;
  } else if (biography.length > 2000) {
    recommendations.push('Biography is quite long - consider focusing on key elements');
    qualityScore -= 5;
  }
  
  const isHighQuality = qualityScore >= 70;
  
  return {
    isHighQuality,
    qualityScore,
    recommendations
  };
};
